import * as SentryCapacitor from "@sentry/capacitor";
import { bs } from "./state/bound.store";
import { captureSentryMessage, isWebPreview } from "./utils";
import { reactAppStage } from "./utils/reactAppStage";

let breadcrumbCounter = 0;

const sentryEnabled = () => {
  if (isWebPreview) return false;
  if (import.meta.env.MODE !== "production") return false;
  if (reactAppStage() === "demo") return true;
  if (reactAppStage() === "test") return true;
  return false;
};

export function initSentry() {
  SentryCapacitor.init({
    dsn: "https://b5ce1302050540cd953cdbf657ce4817@sentry.softpauer.com/2",
    maxBreadcrumbs: 200,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumbCounter > 200) {
        breadcrumbCounter = 0;
        captureSentryMessage("Logs for " + (bs().profile?.email ?? "NO_USER"));
      }
      breadcrumbCounter += 1;
      if ((breadcrumb.message?.length ?? 0) > 10000) {
        breadcrumb.message = breadcrumb?.message?.substring(0, 10000) + "...";
      }
      return breadcrumb;
    },
    release: import.meta.env.VITE_APP_VERSION,
    enabled: sentryEnabled(),
    environment: reactAppStage(),
    _experiments: {
      enableLogs: true,
    },

    // Tracing
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    integrations: [
      SentryCapacitor.browserTracingIntegration(),
      SentryCapacitor.replayIntegration({
        networkDetailAllowUrls: [
          "https://wp.t-i.eventr.softpauer.com",
          "https://wp.d-i.eventr.softpauer.com",
          "https://wp.l.eventr.softpauer.com",
          "https://api.t-i.eventr.softpauer.com",
          "https://api.d-i.eventr.softpauer.com",
          "https://api.l.eventr.softpauer.com",
          "https://account.t-i.eventr.softpauer.com",
          "https://account.d-i.eventr.softpauer.com",
          "https://account.l.eventr.softpauer.com",
        ],
        networkRequestHeaders: ["X-SP-Trace-Id"],
        blockAllMedia: false,
        maskAllText: false,
      }),
    ],
    beforeSend: function (event) {
      const firstException = event.exception?.values?.[0];
      if (!firstException) return event;

      // filter out UnhandledRejection errors that have no information
      if (event.exception?.values?.length === 1 && firstException.type === "UnhandledRejection") {
        if (firstException.value?.startsWith("Object captured as promise rejection with keys")) return null;
        if (firstException.value?.startsWith("Non-Error promise rejection captured with value: ")) return null;
      }
      // filter out AxiosError errors that have no information
      if (firstException.type === "AxiosError") {
        return null;
      }
      if (firstException.type === "InvalidStateError") {
        if (firstException.value === "Skipping view transition because viewport size changed.") {
          return null;
        }
        if (
          firstException.value ===
          "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing."
        ) {
          return null;
        }
      }
      if (firstException.type === "Error") {
        if (firstException.value === "InvalidStateError: Object store cannot be found in the database") {
          return null;
        }
        if (
          firstException.value ===
          "UnknownError: Attempt to delete range from database without an in-progress transaction"
        ) {
          return null;
        }
        if (
          firstException.value ===
          "UnknownError: Connection to Indexed Database server lost. Refresh the page to try again"
        ) {
          return null;
        }
        if (
          firstException.value === "UnknownError: An internal error was encountered in the Indexed Database server"
        ) {
          return null;
        }
        if (
          firstException.value ===
          "UnknownError: Attempt to get a record from database without an in-progress transaction"
        ) {
          return null;
        }
        if (firstException.value === '"LocalNotifications" plugin is not implemented on ios') {
          return null;
        }
        if (firstException.value === '"AppGroupUserDefaults" plugin is not implemented on ios') {
          return null;
        }
      }
      if (firstException.type === "AbortError") {
        if (firstException.value === "Old view transition aborted by new view transition.") {
          return null;
        }
      }
      if (firstException.type === "CanceledError") {
        if (firstException.value === "Request cancelled: No connectivity.") {
          return null;
        }
      }
      if (firstException.type === "SyntaxError") {
        if (
          firstException.value ===
          "Unexpected identifier 'notifyAppReady'. Expected ')' to end an argument list."
        ) {
          return null;
        }
      }

      return event;
    },
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}
